import React from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { CalendarDots, Heartbeat } from "@phosphor-icons/react";

import { USER_ID_PARAM } from "@constants/calendarFilter";
import ModuleHeader from "../ModuleHeader";
import { ModuleContainer } from "../styles";
import BilledHoursChart from "./BilledHoursChart";
import Bullet from "./Bullet";
import EmptySummary from "./EmptySummary";
import { theme } from "@answersnow/pkg-theme";

const { purple, orange } = theme.palette;

const getMinimumHours = (employmentType) => {
  const minimumHours = {
    W2_1_FTE: 28,
    W2_75_FTE: 21,
    W2_5_FTE: 14,
    Clinical_Assessor: 32,
  };

  return minimumHours[employmentType] || null;
};

const SummaryWeekLegend = () => {
  return (
    <Stack columnGap={7} mt={7} ml={{ sm: 0, md: "75px" }} direction={{ sm: "column", md: "row" }}>
      <Box>
        <Typography component="p" variant="caption" color="text.secondary" align="center">
          <Bullet color={purple[800]} />
          Billed hours
        </Typography>
      </Box>

      <Box>
        <Typography component="p" variant="caption" color="text.secondary" align="center">
          <Bullet color={purple[200]} />
          Remaining authorized hours
        </Typography>
      </Box>
    </Stack>
  );
};
/**
 * @component
 * @description `SummaryWeek` component displays delivered hours (completed services from their AN calendar)
 * over total authorized hours across all their assigned clients during the current week.
 *
 * @param {Object} props - The component props.
 * @param {number} props.authorizedHours - The number of authorized hours.
 * @param {string} props.clinicianName - The clinician name.
 * @param {string} props.employmentType - The employment type of the clinician.
 * @param {number} props.deliveredHours - The number of delivered hours.
 * @param {number} props.scheduledHours - The number of scheduled hours.
 * @param {boolean} props.isSmallScreen - Whether the screen is small.
 * @param {number} props.userId - The user id of the clinician.
 * @returns {React.ReactNode} The SummaryWeek component.
 */
const SummaryWeekModule = ({
  authorizedHours,
  clinicianName,
  employmentType,
  deliveredHours,
  scheduledHours,
  isSmallScreen,
  userId,
}) => {
  if (!scheduledHours) {
    return <EmptySummary clinicianName={clinicianName} />;
  }

  const minimumHours = getMinimumHours(employmentType);
  const billedHoursPercentage = authorizedHours ? (deliveredHours / authorizedHours) * 100 : 0;
  const showMinimumHoursText = minimumHours && scheduledHours;
  const remainingForMinimum = showMinimumHoursText ? minimumHours - scheduledHours : 0;

  // Set texts based on the clinician name
  let scheduleText = "Schedule ";
  let youHaveHitYourText = "You've hit your ";
  let viewMyScheduleText = "View My Schedule";
  // Default link to the clinician's schedule
  let viewMyScheduleLink = "/clinician-schedule";

  if (clinicianName) {
    scheduleText = `${clinicianName} can schedule `;
    youHaveHitYourText = `${clinicianName} has hit the `;
    viewMyScheduleText = ` View ${clinicianName}'s schedule`;
    viewMyScheduleLink = `/clinician-schedule?${USER_ID_PARAM}=${userId}`;
  }

  return (
    <Paper component="section" elevation={2} sx={ModuleContainer}>
      <ModuleHeader
        Icon={Heartbeat}
        title="Summary this week"
        subtitle={
          showMinimumHoursText && (
            <>
              {remainingForMinimum > 0 ? (
                <span>
                  {scheduleText}
                  <Typography component="em" variant="subtitle2" color={orange[600]}>
                    {remainingForMinimum} more hours
                  </Typography>{" "}
                  to hit the weekly minimum of {minimumHours} hours.
                </span>
              ) : (
                <span>
                  {youHaveHitYourText} weekly minimum of {minimumHours} hours
                </span>
              )}
            </>
          )
        }
      />

      <Stack direction={isSmallScreen ? "column" : "row"} spacing={7} mt={7} alignItems="center">
        <BilledHoursChart value={billedHoursPercentage} color={purple[800]} />

        {isSmallScreen && <SummaryWeekLegend />}

        <Box pt={1} width="100%">
          <Typography component="h3" variant="h2" fontFamily="TestTiemposText" fontWeight={500}>
            <span style={{ fontSize: "2rem" }}>{deliveredHours}</span> hours billed
          </Typography>

          <Typography component="h4" variant="body1" color="text.secondary">
            / {authorizedHours || 0} hours authorized
          </Typography>

          <Button
            variant="outlined"
            color="inherit"
            sx={{ mt: 7, width: { xs: "100%", md: "auto" } }}
            href={viewMyScheduleLink}
            startIcon={<CalendarDots />}
          >
            {viewMyScheduleText}
          </Button>
        </Box>
      </Stack>

      {!isSmallScreen && <SummaryWeekLegend />}
    </Paper>
  );
};

export default SummaryWeekModule;
